import type { LearnFAQ } from "@/pages/learn/learnShared";
import { makeEsArticle, type EsArticleInput } from "./learnFactoryEs";

/**
 * /es/learn/cognitive-fx-vs-saint-tms
 *
 * Versión en español de /learn/cognitive-fx-vs-saint-tms (PROMPT 6).
 * La ÚNICA página del sitio que nombra a Cognitive FX. Usa únicamente las
 * palabras publicadas por la propia clínica, citadas textualmente y
 * enlazadas a la fuente. Sin opiniones, sin palabras como "falso" o
 "estafa", y nada sobre su atención de conmoción/lesión cerebral.
 * Enlazada solo desde /es/learn/saint-vs-accelerated-tms (no en el menú).
 */

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "3 de noviembre de 2026",
  lastReviewed: "3 de noviembre de 2026",
  lastUpdated: "3 de noviembre de 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: `¿Cognitive FX ofrece SAINT®?`,
    answer: `No. Cognitive FX ofrece su propio protocolo de TMS acelerada guiada por fMRI, que describe como uso off-label y no probado en los ensayos clínicos de SAINT. No figura en el localizador de proveedores de SAINT® de Magnus Medical.`,
  },
  {
    question: `¿Cuánto cuesta la TMS de Cognitive FX en comparación con SAINT®?`,
    answer: `Cognitive FX cotiza $9.900 por su programa de 5 días, con viaje y alojamiento aparte, y afirma que el seguro no se factura. SAINT® en Neuro Wellness comienza en $25.000 por el curso completo, y Medicare tiene una tarifa para SAINT®.`,
  },
  {
    question: `¿La TMS guiada por fMRI de Cognitive FX está autorizada por la FDA?`,
    answer: `Los dispositivos de TMS están autorizados por la FDA, pero Cognitive FX afirma que su calendario acelerado de 5 días, y tratar a adolescentes, son uso off-label. SAINT® es el único sistema autorizado por la FDA para TMS acelerada orientada por fMRI personalizada.`,
  },
  {
    question: `¿Cuál es el costo de Cognitive FX frente al costo de SAINT®?`,
    answer: `Cognitive FX cotiza $9.900 por su programa de 5 días, con viaje y alojamiento aparte, y afirma "Insurance: Not billed" (el seguro no se factura). SAINT® en Neuro Wellness comienza en $25.000 por el curso completo de 5 días, y Medicare tiene una tarifa hospitalaria ambulatoria para SAINT® ($19.703, CMS, julio de 2025). Los dos precios compran dos tratamientos distintos: un precio menor por un tratamiento distinto no es un descuento sobre SAINT®.`,
  },
  {
    question: `¿Cómo se compara la TMS guiada por fMRI de Cognitive FX con la orientación por fMRI de SAINT®?`,
    answer: `Cognitive FX afirma que usa su propio método basado en fMRI "rather than using Magnus Medical's proprietary targeting system" (en lugar del sistema de orientación propio de Magnus Medical). SAINT® usa el software de orientación de SAINT® autorizado por la FDA, que personaliza el objetivo a partir de su resonancia fMRI (510(k) K220177). Usar una resonancia fMRI por sí solo no es lo que convierte un tratamiento en SAINT®.`,
  },
  {
    question: `¿La TMS de Cognitive FX está autorizada por la FDA?`,
    answer: `Los dispositivos de TMS están autorizados por la FDA, pero Cognitive FX describe su programa de 5 días como "our own protocol, delivered off-label" (nuestro propio protocolo, aplicado off-label) y afirma que tratar a adolescentes es off-label. SAINT® es el sistema autorizado por la FDA (510(k) K220177, septiembre de 2022) para TMS acelerada orientada por fMRI personalizada en adultos de 22 años o más con trastorno depresivo mayor.`,
  },
  {
    question: `¿Cognitive FX acepta seguro?`,
    answer: `No. Cognitive FX afirma "Insurance: Not billed" (el seguro no se factura). SAINT® tiene una tarifa hospitalaria ambulatoria de Medicare ($19.703, CMS, julio de 2025), y los planes comerciales pueden aprobarla mediante autorización previa. Verificamos sus beneficios del seguro sin costo antes de que se comprometa.`,
  },
  {
    question: `¿TMS de un día (ONE-D) de Cognitive FX frente a SAINT®?`,
    answer: `SAINT® es un protocolo de 5 días autorizado por la FDA (510(k) K220177). Comprimir el curso en un solo día es un calendario off-label, no SAINT®. Cognitive FX describe su calendario acelerado de 5 días como "our own protocol, delivered off-label"; cualquier versión de un día (ONE-D) es un protocolo distinto y off-label, no una SAINT® más rápida. SAINT® está autorizado para adultos de 22 años o más como un curso de 5 días.`,
  },
];

export const esCognitiveFxVsSaint = makeEsArticle({
  slug: "cognitive-fx-vs-saint-tms",
  title: `Cognitive FX frente a SAINT® TMS: ¿es el mismo tratamiento? (2026)`,
  description: `La TMS acelerada guiada por fMRI de $9.900 de Cognitive FX no es SAINT®. En sus propias palabras: uso off-label, su propio protocolo, no probado en los ensayos de SAINT. Vea la comparación lado a lado.`,
  h1: `Cognitive FX frente a SAINT® TMS: ¿cuál es la diferencia?`,
  breadcrumbLabel: "Cognitive FX vs SAINT® TMS",
  shortAnswer: `No, el programa de 5 días de Cognitive FX no es SAINT®. Cognitive FX, una clínica en Provo, Utah, describe su programa de $9.900 como "our own protocol, delivered off-label" (nuestro propio protocolo, aplicado off-label) y afirma que "our protocol has not been tested in the SAINT clinical trials" (nuestro protocolo no se ha probado en los ensayos clínicos de SAINT). SAINT® es el sistema Magnus autorizado por la FDA, y Neuro Wellness TMS Centers of America es el proveedor verificado de SAINT® en Florida. Si está en crisis, llame o envíe un mensaje de texto al 988.`,
  keyTakeaways: [
    `Cognitive FX describe su TMS de 5 días como "our own protocol, delivered off-label".`,
    `Cognitive FX afirma: "our protocol has not been tested in the SAINT clinical trials".`,
    `Cognitive FX usa su propia orientación por fMRI en lugar del sistema de orientación autorizado por la FDA de Magnus Medical.`,
    `SAINT® está autorizado por la FDA (510(k) K220177) para adultos de 22 años o más con trastorno depresivo mayor.`,
    `Medicare tiene una tarifa hospitalaria ambulatoria para SAINT® ($19.703, CMS, julio de 2025); Cognitive FX afirma que el seguro no se factura.`,
    `Neuro Wellness en Coral Springs, Florida, es un proveedor verificado de SAINT® que figura en el localizador de Magnus Medical.`,
  ],
  sections: [
    {
      heading: "Respuesta rápida",
      paragraphs: [
        `No, el programa de 5 días de Cognitive FX no es SAINT®. Cognitive FX, una clínica en Provo, Utah, describe su programa de $9.900 como "our own protocol, delivered off-label" (nuestro propio protocolo, aplicado off-label) y afirma que "our protocol has not been tested in the SAINT clinical trials" (nuestro protocolo no se ha probado en los ensayos clínicos de SAINT). SAINT® es el sistema Magnus autorizado por la FDA, y Neuro Wellness TMS Centers of America es el proveedor verificado de SAINT® en Florida.`,
      ],
    },
    {
      heading: "Lado a lado",
      paragraphs: [],
      table: {
        headers: [
          "",
          "SAINT® en Neuro Wellness",
          "TMS acelerada guiada por fMRI de Cognitive FX (según lo publicado por Cognitive FX)",
        ],
        rows: [
          [
            "Qué es",
            "SAINT®, autorizado por la FDA (510(k) K220177, septiembre de 2022)",
            `"our own protocol, delivered off-label" [1]`,
          ],
          [
            "Orientación",
            "Software de orientación de SAINT® autorizado por la FDA",
            `"rather than using Magnus Medical's proprietary targeting system, Cognitive FX uses its own fMRI-based method" [1]`,
          ],
          [
            "Evidencia de ensayos",
            `Remisión del 78,6 % en el ensayo controlado aleatorizado de 2022 (Cole et al., Am J Psychiatry 2022;179:132–141)`,
            `"our protocol has not been tested in the SAINT clinical trials" [1]`,
          ],
          [
            "Edades",
            "Adultos de 22 años o más",
            `"adults and teens (14 and up)"; "treating teens, are off-label" [2]`,
          ],
          [
            "Seguro",
            "Tarifa de Medicare ($19.703 hospitalario ambulatorio, CMS, julio de 2025); los planes comerciales pueden aprobar mediante autorización previa",
            `"Insurance: Not billed" [2]`,
          ],
          [
            "Precio",
            "Desde $25.000 por el curso completo de 5 días de SAINT®",
            `"$9,900 for the week, including the fMRI scan, targeting, TMS, CBT, physical therapy, massage, photobiomodulation and follow-up. Travel and lodging are extra." [2]`,
          ],
          [
            "Verificado en el localizador de Magnus Medical",
            "Sí (Florida)",
            "No",
          ],
        ],
      },
    },
    {
      heading: "Por qué los precios no se pueden comparar",
      paragraphs: [
        `$9.900 y $25.000 son precios de dos tratamientos distintos. El precio de Cognitive FX compra su propio protocolo off-label más extras como masaje y fisioterapia. El precio de SAINT® compra el sistema autorizado por la FDA y el protocolo que produjo los resultados de los ensayos. Un precio menor por un tratamiento distinto no es un descuento sobre SAINT®.`,
      ],
    },
    {
      heading: `¿El 79 % de remisión de SAINT aplica a Cognitive FX?`,
      paragraphs: [
        `No. Esos resultados provienen de pacientes de los ensayos de SAINT®. Cognitive FX afirma que su protocolo no se probó en esos ensayos.`,
      ],
    },
    {
      heading: "Costo de Cognitive FX frente al costo de SAINT®",
      paragraphs: [
        `Cognitive FX cotiza $9.900 por su programa de 5 días, con viaje y alojamiento aparte, y afirma "Insurance: Not billed" [2]. SAINT® en Neuro Wellness comienza en $25.000 por el curso completo de 5 días, y Medicare tiene una tarifa hospitalaria ambulatoria para SAINT® de $19.703 (CMS, julio de 2025). Los dos precios compran dos tratamientos distintos: el precio de Cognitive FX compra su propio protocolo off-label más extras como masaje y fisioterapia, mientras que el precio de SAINT® compra el sistema autorizado por la FDA y el protocolo que produjo los resultados de los ensayos. Un precio menor por un tratamiento distinto no es un descuento sobre SAINT®.`,
      ],
    },
    {
      heading:
        "TMS guiada por fMRI de Cognitive FX frente a la orientación por fMRI de SAINT®",
      paragraphs: [
        `Cognitive FX afirma que usa su propio método basado en fMRI "rather than using Magnus Medical's proprietary targeting system" [1]. SAINT® usa el software de orientación de SAINT® autorizado por la FDA, que personaliza el objetivo a partir de su resonancia fMRI (510(k) K220177). Usar una resonancia fMRI por sí solo no es lo que convierte un tratamiento en SAINT® — el software de orientación autorizado por la FDA y el protocolo probado en los ensayos de SAINT sí lo son.`,
      ],
    },
    {
      heading: "¿La TMS de Cognitive FX está autorizada por la FDA?",
      paragraphs: [
        `Los dispositivos de TMS están autorizados por la FDA, pero Cognitive FX describe su programa de 5 días como "our own protocol, delivered off-label" [1] y afirma que tratar a adolescentes es off-label [2]. SAINT® es el sistema autorizado por la FDA (510(k) K220177, septiembre de 2022) para TMS acelerada orientada por fMRI personalizada, autorizado para adultos de 22 años o más con trastorno depresivo mayor. Un calendario off-label no es lo mismo que un tratamiento autorizado por la FDA.`,
      ],
    },
    {
      heading: "¿Cognitive FX acepta seguro?",
      paragraphs: [
        `No. Cognitive FX afirma "Insurance: Not billed" [2]. SAINT® tiene una tarifa hospitalaria ambulatoria de Medicare ($19.703, CMS, julio de 2025), y los planes comerciales pueden aprobarla mediante autorización previa. Verificamos sus beneficios del seguro sin costo antes de que se comprometa.`,
      ],
    },
    {
      heading: "TMS de un día (ONE-D) de Cognitive FX frente a SAINT®",
      paragraphs: [
        `SAINT® es un protocolo de 5 días autorizado por la FDA (510(k) K220177). Comprimir el curso en un solo día es un calendario off-label, no SAINT®. Cognitive FX describe su calendario acelerado de 5 días como "our own protocol, delivered off-label" [1]; cualquier versión de un día (ONE-D) es un protocolo distinto y off-label, no una SAINT® más rápida. SAINT® está autorizado para adultos de 22 años o más como un curso de 5 días.`,
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "[1] Cognitive FX. SAINT Treatment Cost: How Does It Compare to Other Accelerated TMS? Publicado por Cognitive FX, Provo, UT. Consultado el 3 de noviembre de 2026.",
      url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
    },
    {
      citation:
        "[2] Cognitive FX. Precision Mental Health: fMRI-Guided Transcranial Magnetic Stimulation. Publicado por Cognitive FX, Provo, UT. Consultado el 3 de noviembre de 2026.",
      url: "https://www.cognitivefxusa.com/precision-mental-health-fmri-guided-transcranial-magnetic-stim",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, septiembre de 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Centers for Medicare & Medicaid Services. Hospital Outpatient Prospective Payment System — tarifa de SAINT TMS (CMS, julio de 2025).",
    },
  ],
  relatedLinks: [
    {
      title: "Aviso de SAINT\u00ae: los programas parecidos no son SAINT\u00ae",
      href: "/es/learn/saint-disclaimer",
      description:
        "Nuestro aviso formal: Cognitive FX y otros programas \u201cguiados por fMRI\u201d no son SAINT\u00ae",
    },
    {
      title: `SAINT\u00ae frente a la TMS acelerada de $9,900: no es el mismo tratamiento`,
      href: "/es/learn/saint-vs-accelerated-tms",
      description:
        "Comparaci\u00f3n lado a lado de estado ante la FDA, orientaci\u00f3n y evidencia de ensayos",
    },
    {
      title:
        "\u00bfEs realmente SAINT\u00ae? C\u00f3mo verificar una cl\u00ednica",
      href: "/es/learn/what-is-a-verified-saint-provider",
      description:
        "Una verificaci\u00f3n de 3 pasos y se\u00f1ales de alerta antes de pagar",
    },
    {
      title: "\u00bfQui\u00e9n califica para SAINT\u00ae TMS?",
      href: "/es/learn/who-qualifies-for-saint-tms",
      description: "Elegibilidad, listas y pr\u00f3ximos pasos",
    },
    {
      title: "\u00bfQu\u00e9 es SAINT\u00ae TMS?",
      href: "/es/learn/what-is-saint-tms",
      description:
        "El protocolo de 5 d\u00edas guiado por fMRI autorizado por la FDA, explicado",
    },
    {
      title: "Costo de SAINT\u00ae TMS",
      href: "/es/learn/saint-tms-cost",
      description:
        "Qu\u00e9 cuesta SAINT\u00ae, informaci\u00f3n de Medicare y opciones de pago",
    },
  ],
  reviewBlock,
  toc: [
    { label: "Respuesta rápida", href: "#respuesta-rapida" },
    { label: "Lado a lado", href: "#lado-a-lado" },
    {
      label: "Por qué los precios no se comparan",
      href: "#por-que-los-precios-no-se-pueden-comparar",
    },
    {
      label: "¿El 79 % aplica a Cognitive FX?",
      href: "#el-79-de-remision-de-saint-aplica-a-cognitive-fx",
    },
    {
      label: "Costo de Cognitive FX vs SAINT®",
      href: "#costo-de-cognitive-fx-frente-al-costo-de-saint",
    },
    {
      label: "fMRI de Cognitive FX vs SAINT®",
      href: "#tms-guiada-por-fmri-de-cognitive-fx-frente-a-la-orientacion-por-fmri-de-saint",
    },
    {
      label: "¿Autorizada por la FDA?",
      href: "#la-tms-de-cognitive-fx-esta-autorizada-por-la-fda",
    },
    {
      label: "¿Acepta seguro?",
      href: "#cognitive-fx-acepta-seguro",
    },
    {
      label: "ONE-D vs SAINT®",
      href: "#tms-de-un-dia-one-d-de-cognitive-fx-frente-a-saint",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® no es para todos. Está contraindicado para personas con metal sensible a campos magnéticos no extraíble cerca de la cabeza, ciertos dispositivos implantados o presión intracraneal aumentada. Los posibles efectos secundarios incluyen cefalea, fatiga, molestias en el cuero cabelludo y ansiedad; la TMS conlleva un pequeño riesgo de convulsión y, como con cualquier tratamiento para la depresión, un riesgo de empeoramiento o pensamientos suicidas. Si está en crisis, llame o envíe un mensaje de texto al 988.",
  trademarkFooter:
    "Cognitive FX es una marca registrada de su propietario. SAINT® es una marca registrada de Magnus Medical. Neuro Wellness TMS Centers of America no está afiliada a Cognitive FX. Los resultados individuales varían; no se garantiza ningún resultado. La cobertura varía según el plan. Las citas de los materiales publicados de Cognitive FX se consultaron el 3 de noviembre de 2026.",
  ctaBand: {
    headline: "Vea si SAINT® es para usted, sin costo",
    primaryText: "Vea si puedo ser candidato",
    primaryHref: "/es/learn/who-qualifies-for-saint-tms",
    secondaryText: "Hable con una persona",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
} satisfies EsArticleInput);

// Añadir esquema Article con datePublished + dateModified
const baseGraph =
  (esCognitiveFxVsSaint.schemaJson as { "@graph": unknown[] })["@graph"] || [];
esCognitiveFxVsSaint.schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    ...baseGraph,
    {
      "@type": "Article",
      "@id":
        "https://neurowellnesstms.com/es/learn/cognitive-fx-vs-saint-tms#article",
      headline: "Cognitive FX frente a SAINT® TMS: ¿cuál es la diferencia?",
      description:
        "La TMS acelerada guiada por fMRI de $9.900 de Cognitive FX no es SAINT®. En sus propias palabras: uso off-label, su propio protocolo, no probado en los ensayos de SAINT. Vea la comparación lado a lado.",
      datePublished: "2026-11-03",
      dateModified: "2026-11-03",
      mainEntityOfPage:
        "https://neurowellnesstms.com/es/learn/cognitive-fx-vs-saint-tms",
      author: {
        "@type": "MedicalOrganization",
        name: "Neuro Wellness TMS Centers of America",
      },
      publisher: {
        "@type": "MedicalOrganization",
        name: "Neuro Wellness TMS Centers of America",
        url: "https://neurowellnesstms.com/",
      },
    },
  ],
};
